import "./style.css";
import axios from 'axios';
import logo from "./Helinox- _White.png";
import { backgroundForThisWeek } from "./loginBackgrounds";
/* The vertical Helinox logo, mark over wordmark (docs/Helinox Logo_BK_Vertical layout_20260713.svg) — Max, 2026-10-19. */
import helinoxLogo from "./helinox_logo_vertical.svg";
import dacLogo from "./dac_logo.svg";
import dacLogoDark from "./dac_logo_dark.svg";
/* The mark on the partner card. Jay Jung offered a handshake emoji and asked for a better
   idea if there was one — an emoji renders as a different picture on every machine and
   carries nobody's brand, so this is the same drawing as a line icon: one stroke weight,
   one colour, the same box as the Eclipse opposite it. */
import { Handshake, Globe, Instagram, Facebook, TreePine, Gift } from "lucide-react";
import PasswordField from './PasswordField';
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BACKEND_URL, getAuthCheckUrl } from "./backendConfig";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://13.67.118.92:5000";
const GOOGLE_LOGIN_URL = process.env.REACT_APP_GOOGLE_LOGIN_URL || `${BACKEND_URL}/auth/google`;
const hasGoogleLogin = Boolean(GOOGLE_LOGIN_URL);
const APP_LOGIN_VERSION = '20260606-login-field-label-v2';
/* The VALUES are dictionary keys (src/locales/xAuth.js), read where the message is drawn, so it follows the
   portal's language; the code in the URL is what the page keeps. */
const LOGIN_ERROR_MESSAGES = {
    /* No line for a session that ran out (Jay Jung, 2026-10-19, on the red sentence under the DAC button: "No need
       this. Please delete"). sessionGuard still brings the person back to the page they were on after signing in;
       the cards are all a person who was signed out needs to see. */
    google_domain: 'xAuth.errGoogleDomain',
    google_unverified: 'xAuth.errGoogleUnverified',
    google_denied: 'xAuth.errGoogleDenied',
    google_account_inactive: 'xAuth.errGoogleInactive',
    // a partner account made by its first sign-in waits for Jay or Max (Jay task #1155)
    account_pending: 'xAuth.errAccountPending',
    google_config: 'xAuth.errGoogleConfig',
    google_auth_failed: 'xAuth.errGoogleFailed',
    /* The partner's own Google sign-in (Jay, 2026-10-17, DAC VN): its own OAuth client and its own
       answers, because "use a Helinox email" is the wrong thing to tell somebody from a partner. */
    partner_config: 'xAuth.errPartnerConfig',
    partner_domain: 'xAuth.errPartnerDomain',
    partner_off: 'xAuth.errPartnerOff',
    partner_no_account: 'xAuth.errPartnerNoAccount',
};
/* The drivers' own door (Jay, 2026-10-20: drivers sign in with their personal Google account). Vietnamese first:
   it is read by the drivers — and so it is NOT a dictionary key: it reads the same in every language. */
const DRIVER_ERROR_TEXT = {
    driver_unknown: 'Tài khoản Google này chưa được gắn với tài xế nào. Hãy gửi địa chỉ Gmail của bạn cho người quản lý Driver\'s Log rồi đăng nhập lại. · This Google account is not on a driver\'s record yet.',
};
const knownLoginError = (code) => !!(code && (LOGIN_ERROR_MESSAGES[code] || DRIVER_ERROR_TEXT[code]));
/* The server's own answer to a wrong password (and the page's fallback). Compared, not shown: the driver card
   prints its Vietnamese line for it, the Partner card the dictionary's. */
const INVALID_LOGIN = 'Invalid username or password';
const PARTNER_GOOGLE_URL = `${BACKEND_URL}/auth/google/partner`;
/* THE DRIVERS' DOOR: the second (External) Google client, because a Gmail address cannot use the portal's own
   Internal one. Shown on its own when the page was reached on the way to the Driver's Log. */
const DRIVER_GOOGLE_URL = `${BACKEND_URL}/auth/google/driver`;
const DRIVERLOG_ON = process.env.REACT_APP_DRIVERLOG === '1';
// Staging only, with the track itself (REACT_APP_PARTNER; see index.js).
const PARTNER_ON = process.env.REACT_APP_PARTNER === '1';

function GoogleIcon() {
    return (
        <svg className="google-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
                fill="#FBBC05"
                d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
            />
            <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z"
            />
        </svg>
    );
}

function Login() {
    const { t } = useTranslation();

    const [values, setValues] = useState({
        username: '',
        password: ''
    });

    const [error, setError] = useState('');
    const [oauthError, setOauthError] = useState('');
    const [loading, setLoading] = useState(false);
    /* REMEMBER FOR 30 DAYS — Max, 2026-10-07, who asked for it and then sent the wording he
       wanted: the label NAMES THE DURATION. "Remember me" asks somebody to agree to
       something without saying what; this one says what they are agreeing to, which
       matters here because the portal holds FOB prices, vendor names and costings and the
       cookie is the whole credential.
       OFF BY DEFAULT. The long session is the one that has to be chosen. */
    const [remember, setRemember] = useState(false);
    const [returnTo, setReturnTo] = useState('');
    /* The short-lived token /login hands back when the account is still on `1@`. Holding it in
       state and nowhere else is deliberate: it is spent on the next request and never stored. */
    const [changeToken, setChangeToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    /* True until /portal-auth has answered — the cards are not drawn before then, or somebody who is
       already signed in sees the form for a moment and is then taken away from it. */
    const [checking, setChecking] = useState(true);
    const [confirmPassword, setConfirmPassword] = useState('');
    /* ON THE WAY TO THE DRIVER'S LOG (a driver's phone, whose 30-day session ran out, or the link the drivers are sent):
       one card with one button instead of three ways in — Jay: "Someone new should be able to use it without
       explanation". Anybody else reaching it opens the ordinary cards with the link under it. */
    const [driverMode, setDriverMode] = useState(false);

    useEffect(() => {
        const storedVersion = localStorage.getItem('hevn_login_version');
        const reloadKey = `hevn_login_reloaded_${APP_LOGIN_VERSION}`;
        const params = new URLSearchParams(window.location.search);
        const loginError = params.get('error');
        const requestedReturnTo = params.get('returnTo');

        /* A Google answer is shown under the Google buttons, a password answer in the form — since the
           DAC button moved under Helinox's (2026-10-19), "your DAC account does not exist" printed in the
           Partner card's password form would be read beside the wrong door. */
        // The CODE is kept, and worded where it is drawn (oauthErrorText below).
        if (knownLoginError(loginError)) {
            setOauthError(loginError);
        }

        /* A path on THIS site only: "//elsewhere.com" also starts with a slash and a browser reads it as
           another host, and now that a signed-in visitor is sent on without a click (below) that would
           be a redirect anybody could hand out. Back to /login itself would go round for ever. */
        const safeReturnTo = requestedReturnTo && requestedReturnTo.startsWith('/')
            && !requestedReturnTo.startsWith('//') && !requestedReturnTo.startsWith('/\\')
            && !requestedReturnTo.startsWith('/login') ? requestedReturnTo : '';
        if (safeReturnTo) {
            setReturnTo(safeReturnTo);
        }
        if (DRIVERLOG_ON && (safeReturnTo.startsWith('/driver-log') || (loginError || '').startsWith('driver_'))) {
            setDriverMode(true);
        }

        if (storedVersion && storedVersion !== APP_LOGIN_VERSION && !sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.replace(`/login?v=${APP_LOGIN_VERSION}`);
            return;
        }

        localStorage.setItem('hevn_login_version', APP_LOGIN_VERSION);

        /* ALREADY SIGNED IN? THEN THIS PAGE HAS NOTHING TO ASK (Jay, 2026-10-16: "whenever I close the
           Chrome tab and re-open it, I should re-login"). He was not signed out: the session cookie
           outlives the tab (2 hours of idle time, or 30 days when remembered). Measured on his own
           morning — signed in at 12:19, working until 12:34, and at 12:45 a fresh tab loaded
           /login?returnTo=%2F with no referer and nothing before it: the URL Chrome offers first,
           because every sign-in passes through it. This page never asked whether the cookie was
           still good, so a live session was shown a sign-in form and signed in a second time.
           Not when the URL carries an error the page has words for: then the person has to read why they are here. */
        if (knownLoginError(loginError)) {
            setChecking(false);
            return;
        }
        let gone = false;
        axios.get(getAuthCheckUrl(), { withCredentials: true })
            .then((res) => {
                if (gone) return;
                if (res.data && res.data.status === 'Success') {
                    window.location.replace(safeReturnTo || '/');
                    return;
                }
                setChecking(false);
            })
            .catch(() => { if (!gone) setChecking(false); });
        return () => { gone = true; };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({
            ...values,
            [name]: value
        });

        if (error) {
            setError('');
        }
    };

    const handleGoogleLogin = () => {
        const googleUrl = new URL(GOOGLE_LOGIN_URL, window.location.origin);
        if (returnTo) {
            googleUrl.searchParams.set('returnTo', returnTo);
        }
        window.location.href = googleUrl.toString();
    };

    /* The second step, shown in place of the two cards once the server says the account is still on
       its first password. Kept on this screen rather than on a route of its own: it is one field,
       it cannot be reached without the token, and a URL would invite somebody to bookmark it. */
    const handleSetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError(t('xAuth.pwMismatch'));
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${BACKEND_URL}/login/set-password`,
                { changeToken, password: newPassword },
                { withCredentials: true });
            window.location.replace(returnTo || '/');
        } catch (err) {
            setError(err.response?.data?.error || t('xAuth.setPwFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
    
        try {
            const response = await axios.post(`${BACKEND_URL}/login`, {
                username: values.username,
                password: values.password,
                remember,
            }, {
                withCredentials: true,
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
    
            /* STILL ON THE PASSWORD THE ACCOUNT WAS CREATED WITH. The server answers 200 with no
               session — the password was right, but `1@` is the same for everybody, so it gets
               somebody as far as this screen and no further. */
            if (response.data?.mustChangePassword) {
                setChangeToken(response.data.changeToken);
                setError('');
                return;
            }
            if (response.status === 200) {
                window.location.replace(returnTo || '/');
            }
        } catch (error) {
            console.error('Login failed', error);
            // switched off / waiting for approval (Max 02/11: refused at every door) — said in the reader's language
            const d = error.response?.data || {};
            setError(d.pending ? t('xAuth.errAccountPending') : d.inactive ? t('xAuth.errGoogleInactive') : (d.error || INVALID_LOGIN));
        } finally {
            // Clear password
            setValues(prev => ({
                ...prev,
                password: ''
            }));
            setLoading(false);
        }
    };

    const oauthErrorText = oauthError
        ? (DRIVER_ERROR_TEXT[oauthError] || (LOGIN_ERROR_MESSAGES[oauthError] ? t(LOGIN_ERROR_MESSAGES[oauthError]) : oauthError))
        : '';

    return (
        /* The photograph is set here rather than in the stylesheet because which one it is
           depends on the week — see loginBackgrounds.js. Read once per render, not once per
           module: a tab left open over a weekend would otherwise keep the old one. */
        <div className="login-page" style={{ backgroundImage: `url(${backgroundForThisWeek()})` }}>
            <header className="login-topbar">
                <div className="login-topbar-brand">
                    <img src={logo} alt="Helinox" className="login-logo" />
                    <span>HEVN PORTAL</span>
                </div>
            </header>
            {/* the Christmas greeting (season.js) — in the page always, shown by theme-christmas.css only in season */}
            <span className="xmas-snow-page" aria-hidden="true" />
            <p className="xmas-ribbon" role="note"><TreePine size={16} aria-hidden="true" /> {t('xShell.xmasGreeting')} <Gift size={16} aria-hidden="true" /></p>

            {/* SET YOUR OWN PASSWORD, in place of everything else. Nothing to pick from here:
                the account is real, the password that got this far is the one everybody starts on,
                and the only move is to replace it. */}
            {checking ? null : changeToken ? (
            <main className={`login-card${driverMode ? ' login-driver' : ''}`} id="setPassword">
                <h1 className="login-title">{driverMode ? 'Đặt mật khẩu của bạn' : t('xAuth.setPwTitle')}</h1>
                <p className="login-subtitle">
                    {driverMode ? 'Lần đầu đăng nhập: chọn mật khẩu riêng để dùng từ nay về sau.'
                        : t('xAuth.setPwSub')}
                </p>
                <div className="login-card-body">
                    <form className="login-form" onSubmit={handleSetPassword}>
                        <div className="login-field-group">
                            <span className="login-field-label" id="newpw-label">{driverMode ? 'Mật khẩu mới' : t('xAuth.newPassword')}</span>
                            <PasswordField
                                id="newpw" labelId="newpw-label" what={t('xAuth.whatNewPassword')}
                                autoComplete="new-password" placeholder={t('xAuth.phAtLeast8')}
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label" id="confpw-label">{driverMode ? 'Nhập lại' : t('xAuth.confirm')}</span>
                            <PasswordField
                                id="confpw" labelId="confpw-label" what={t('xAuth.whatConfirmation')}
                                autoComplete="new-password" placeholder={t('xAuth.phTypeAgain')}
                                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>
                        <p className="login-hint">
                            {driverMode ? 'Ít nhất 8 ký tự, có chữ hoa, chữ thường và chữ số (VD: Luan2026a).'
                                : t('xAuth.pwRuleHint')}
                        </p>
                        {error && <div className="login-error-message">{error}</div>}
                        <button type="submit" className="login-submit-button" disabled={loading}>
                            {loading ? (driverMode ? 'Đang lưu…' : t('xAuth.saving')) : (driverMode ? 'Lưu và vào nhật ký xe' : t('xAuth.saveContinue'))}
                        </button>
                    </form>
                </div>
            </main>
            ) : driverMode ? (
            <main className="login-card login-driver" id="signInDriver">
                <h1 className="login-title">Tài xế</h1>
                <p className="login-subtitle">
                    Nhật ký xe (Driver&apos;s Log). Đăng nhập một lần bằng Google hoặc tên đăng nhập.
                </p>
                <div className="login-card-body">
                    <button
                        type="button"
                        className="google-login-button login-driver-button"
                        onClick={() => {
                            const u = new URL(DRIVER_GOOGLE_URL, window.location.origin);
                            u.searchParams.set('returnTo', returnTo || '/driver-log');
                            window.location.href = u.toString();
                        }}
                    >
                        <GoogleIcon />
                        <span>Đăng nhập bằng Google</span>
                    </button>
                    {oauthError && <div className="login-error-message login-oauth-error">{oauthErrorText}</div>}
                    {/* OR THE USERNAME AND PASSWORD HUE HANDS OVER (Max, 2026-10-20: "cho 2 anh user name và password luôn
                        nhé"). The first one is the portal's shared first password, so the screen after it asks for the
                        driver's own; a driver's session is kept 30 days either way. */}
                    <div className="login-driver-or"><span>hoặc</span></div>
                    <form className="login-form login-driver-form" onSubmit={handleSubmit} autoComplete="on" data-testid="driver-pw-form">
                        <label className="login-driver-field">
                            <span>Tên đăng nhập</span>
                            <input type="text" name="username" autoComplete="username" autoCapitalize="none" autoCorrect="off"
                                   spellCheck="false" value={values.username} onChange={handleChange} required data-testid="driver-username" />
                        </label>
                        <label className="login-driver-field">
                            <span>Mật khẩu</span>
                            <input type="password" name="password" autoComplete="current-password" value={values.password}
                                   onChange={handleChange} required data-testid="driver-password" />
                        </label>
                        {error && <div className="login-error-message">{error === INVALID_LOGIN ? 'Sai tên đăng nhập hoặc mật khẩu.' : error}</div>}
                        <button type="submit" className="login-submit-button login-driver-submit" disabled={loading} data-testid="driver-pw-submit">
                            {loading ? 'Đang đăng nhập…' : 'Đăng nhập'}
                        </button>
                    </form>
                    <button type="button" className="login-driver-other" onClick={() => setDriverMode(false)}>
                        Không phải tài xế? Đăng nhập HEVN Portal · Not a driver? Sign in to the portal
                    </button>
                </div>
            </main>
            ) : (
            /* A GRID, so the two cards are the SAME HEIGHT — Jay Jung, 2026-10-06: "Helinox is too
               small. Make it bigger 50:50. Also give a little more space between two cards."
               Measured before: Helinox 197px against Non-Helinox 348px, because one holds a button
               and the other holds a form. `grid-auto-rows: 1fr` makes both rows the height of the
               taller one, which keeps them equal whatever either card comes to hold later — a
               min-height fixed at today's 348 would drift the first time somebody adds a line. */
            <div className="login-stack">
            {/* TWO CARDS, NOT ONE — Jay Jung, 2026-09-08 and again on 2026-10-06: "separate the
                cards as Helinox log-in (Google) & Non-Helinox Log-in (ID / PW)". One card with a
                Google button over an ID/PW form reads as two ways for the same person, which is the
                opposite of true: Helinox staff sign in with Google and the ID/PW box is for the
                accounts Max creates by hand for people outside the company. */}
            {hasGoogleLogin && (
            <main className="login-card" id="signIn">
                {/* THE ECLIPSE, and a drawn mark opposite it — Jay Jung, 2026-10-06: "for Helinox
                    log-in, add Eclipse logo and add some icon for Helinox Partners log-in". Both
                    sit in the same 56px box above the title so the two cards read as a pair
                    rather than as one card with a logo and one without. */}
                {/* THE BRAND'S OWN VERTICAL LOGO in place of the Eclipse over a typed "Helinox" (Max, 2026-10-19: "sửa lại
                    logo của helinox"). The heading is the logo, so the card still has one, named by its alt. */}
                <h1 className="login-title login-logo-title"><img src={helinoxLogo} alt="Helinox" className="login-logo-vertical" /></h1>
                <p className="login-subtitle">
                    {t('xAuth.helinoxSub')}
                </p>
                <div className="login-card-body">
                    <button
                        type="button"
                        className="google-login-button"
                        onClick={handleGoogleLogin}
                    >
                        <GoogleIcon />
                        <span>{t('xAuth.signInGoogle')}</span>
                    </button>
                    {/* DAC UNDER HELINOX — Jay Jung, 2026-10-19, a red box drawn here: "Please make DAC log-in
                        box below Helinox's. There are 2 DAC email domain. @dacpole.com is DAC Korea and
                        @dacpole.vn is DAC VN. Please let both can log-in." DAC is the group's own frame
                        maker, not a vendor, so it sits with Helinox and the Partner card keeps the
                        vendors. The button is the partner Google sign-in (its own OAuth client); which
                        domains it admits is the server's table. No note under it (Max: "ko cần note"). */}
                    {PARTNER_ON && (
                        <div className="login-dac">
                            {/* A plain line, then DAC's own logo UNDER it, over its button — Max, 2026-10-19
                                (first the logo stood in the middle of the line: "cho logo DAC dưới dấu gạch
                                phân cách á"). */}
                            <div className="login-dac-rule" aria-hidden="true" />
                            {/* Two pictures, one shown per theme: the logo's black letters vanish on the dark card,
                                and a filter cannot whiten the black without turning the red to cyan. */}
                            <img src={dacLogo} alt="DAC" className="login-dac-logo login-dac-logo-light" />
                            <img src={dacLogoDark} alt="DAC" className="login-dac-logo login-dac-logo-dark" />
                            <button
                                type="button"
                                className="google-login-button login-dac-button"
                                onClick={() => { window.location.href = PARTNER_GOOGLE_URL; }}
                            >
                                <GoogleIcon />
                                {/* "Sign in with Google", as Helinox's — the DAC logo above it already says whose
                                    (Jay Jung, 2026-10-19, circling "DAC" on the button: "No need this"). */}
                                <span>{t('xAuth.signInGoogle')}</span>
                            </button>
                        </div>
                    )}
                    {oauthError && <div className="login-error-message login-oauth-error">{oauthErrorText}</div>}
                    {/* MEET THE COMMUNITY, INSIDE THE CARD — Max, 2026-10-07: first "phần trống
                        đó chúng ta thêm website, facebook và instagram", then, pointing at the
                        row sitting under the pair, "ý tôi là bỏ lên đây nè". It fills the space
                        the Helinox card has and the Partner card does not, which is the whole
                        reason that space existed.
                        The links open away from the portal, so rel noopener. */}
                    <div className="login-social">
                        <span>{t('xAuth.community')}</span>
                        <div className="login-social-row">
                            <a href="https://helinox.com" target="_blank" rel="noopener noreferrer"
                               aria-label={t('xAuth.socialWeb')}><Globe size={17} /></a>
                            <a href="https://www.instagram.com/helinox/" target="_blank"
                               rel="noopener noreferrer"
                               aria-label={t('xAuth.socialIg')}><Instagram size={17} /></a>
                            <a href="https://www.facebook.com/helinox" target="_blank"
                               rel="noopener noreferrer"
                               aria-label={t('xAuth.socialFb')}><Facebook size={17} /></a>
                        </div>
                    </div>
                </div>
            </main>
            )}

            <main className="login-card login-card-second" id="signInVendor">
                <span className="login-mark login-mark-icon" aria-hidden="true">
                    <Handshake size={36} strokeWidth={1.5} />
                </span>
                {/* HELINOX PARTNER, NOT NON-HELINOX — Jay Jung, 2026-10-06: "(Don't say
                    Non-Helinox)" and then "non-Helinox --> Helinox Partner". The old name said
                    what these people are not; this one says what they are, and it is the word the
                    company already uses for them. */}
                <h1 className="login-title">{t('xAuth.partnerTitle')}</h1>
                <p className="login-subtitle">
                    {t('xAuth.partnerSub')}
                </p>
                <div className="login-card-body">
                    {/* The company-Google button that stood here (2026-10-17) moved to the Helinox card as
                        "Sign in with DAC Google" on 2026-10-19 — DAC was the only company it served. This
                        card is the vendors' ID / password. */}
                    <form
                        id="password-login-form"
                        className="login-form"
                        onSubmit={handleSubmit}
                        autoComplete="on"
                    >
                        <div className="login-field-group">
                            <span className="login-field-label" id="username-label">{t('xAuth.username')}</span>
                            <input
                                type="text"
                                name="username"
                                id="username"
                                aria-labelledby="username-label"
                                autoComplete="username"
                                autoCorrect="off"
                                spellCheck="false"
                                placeholder={t('xAuth.username')}
                                value={values.username}
                                onChange={handleChange}
                                className="login-field"
                                required
                            />
                        </div>
                        <div className="login-field-group">
                            <span className="login-field-label" id="password-label">{t('xAuth.password')}</span>
                            <PasswordField
                                id="password"
                                labelId="password-label"
                                autoComplete="current-password"
                                placeholder={t('xAuth.password')}
                                value={values.password}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        {error && (
                            <div className="login-error-message">
                                {error === INVALID_LOGIN ? t('xAuth.invalidLogin') : error}
                            </div>
                        )}
                        {/* The two sit on ONE row, as the reference Max sent does: they are the
                            two things somebody does at this moment instead of typing, and the
                            empty half-row beside the link was what he noticed. */}
                        <div className="login-remember-row">
                            <label className="login-remember">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                />
                                {t('xAuth.remember30')}
                            </label>
                            <Link to="/reset-password-request">{t('xAuth.forgotPw')}</Link>
                        </div>
                        <button
                            type="submit"
                            className="login-submit-button"
                            disabled={loading}
                        >
                            {loading ? t('xAuth.signingIn') : t('xAuth.signIn')}
                        </button>
                    </form>
                </div>
                {/* The same question from the other side, and the opposite answer: a partner
                    account is made by hand, so there is somebody to ask. Both cards end on a line
                    in the same place, which is what lets them stay the same height without one of
                    them looking unfinished. */}
                {/* THE WAY IN FOR SOMEBODY WHO HAS NOBODY TO ASK — Jay Jung, 2026-10-07, after
                    being told an admin creates partner accounts by hand: "How about guest
                    register by themselves first and then admin review/edit/approve." The line
                    used to end the conversation; it now opens one. */}
                <p className="login-card-foot">
                    {t('xAuth.noAccount')} <Link to="/request-access">{t('xAuth.requestAccess')}</Link>
                </p>
            </main>

            </div>
            )}
        </div>
    );
}

export default Login;
